import { useEffect, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Start every screen at the top.
 *
 * React Router keeps the window's scroll offset across navigations, so leaving
 * a long page from a call to action near its foot lands you part-way down the
 * next one. On this campaign that meant arriving at the inspection screen
 * already scrolled past the video and the first manipulation step — the two
 * things the reader is there for.
 *
 * `<ScrollRestoration>` would cover this, but it is only available to data
 * routers and the app uses `BrowserRouter` + `useRoutes`.
 *
 * The browser's own restoration is switched off as well, so a reload or a back
 * navigation also starts at the top. That is the right trade for a three-screen
 * flow whose pages are read start to finish; it would be the wrong one for a
 * long list a reader expects to return to in place.
 */
export const useScrollToTop = (): void => {
  const { pathname } = useLocation()

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
  }, [])

  // Before paint, so the new screen is never briefly visible at the old offset.
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
}

export default useScrollToTop
